import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import DualMultiSelect from "components/Forms/Elements/DualMultiSelect";
import isEmpty from "lodash/isEmpty";
import has from "lodash/has";
import { getAllUsers } from "store/actions/userAction";
import { getAllTeams } from "store/actions/teamAction";
import ErrorLabel from "components/Forms/Elements/ErrorLabel";
import Notification from "components/Notification";
import Translate from "helpers/Translate";

function Desktop({
  clientId,
  userWorkflow = {},
  teamWorkflowsLength,
  ignoredTeams = [],
  onUserSelect,
  onTeamSelect,
  onUserRemove,
  onTeamRemove,
  removeSucceedingWorkflows,
  errorWorkflow
}) {
  const users = useSelector(state => state.user.getAllUsers);
  const teams = useSelector(state => state.team.getAllTeams);
  const [showTeamBox, setShowTeamBox] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      getAllUsers({
        clientId,
        userType: "desktop"
      })
    );
  }, [clientId, dispatch]);

  return (
    <div className="workflow my-30">
      {/* <!-- Column 1 --> */}
      <div className="col-1">
        <h2 className="title mb-20">
          {Translate({
            id: "select_desktop_users_who_will_submit_data_for_review"
          })}
        </h2>
        <div className="row">
          <div className="deo">
            <div className="form-group mb-10">
              <DualMultiSelect
                selectItemsLabel={Translate({ id: "select_desktop_users" })}
                selectedItemsLabel={Translate({ id: "selected_desktop_users" })}
                placeholder={Translate({ id: "search_name_username" })}
                items={
                  has(users, "data")
                    ? users.data.map(u => ({
                        label: u.fullname,
                        tooltip: u.email,
                        value: u.id
                      }))
                    : []
                }
                selectedItems={userWorkflow.source_users || []}
                onSearch={e => {
                  const target = e.target;
                  dispatch(
                    getAllUsers({
                      clientId,
                      name: target.value,
                      userType: "desktop"
                    })
                  );
                }}
                onSearchReturnKey={() => {}}
                onRemoveItem={userId => onUserRemove(userId)}
                onSelect={item => {
                  onUserSelect({
                    label: item.label,
                    tooltip: item.tooltip,
                    value: item.value
                  });
                }}
                errors={isEmpty(userWorkflow.source_users) ? errorWorkflow : {}}
                errorKey="workflows"
              />
            </div>
          </div>
        </div>
      </div>

      {/* <!-- Column 2 --> */}
      <div className="col-2">
        <i className="fas fa-long-arrow-alt-right"></i>
      </div>

      {/* <!-- Column 3 --> */}
      <div className="col-3">
        <h2 className="title mb-20">
          {Translate({
            id: "select_any_one_team_that_will_be_responsible_to_review"
          })}
        </h2>
        <div className="team">
          <div className="form-group mb-10">
            {showTeamBox ? (
              <DualMultiSelect
                selectItemsLabel={Translate({ id: "select_a_team" })}
                selectedItemsLabel={Translate({ id: "selected_teams" })}
                placeholder={Translate({ id: "search_team_name" })}
                onSearch={e => {
                  const target = e.target;
                  dispatch(
                    getAllTeams({
                      clientId,
                      ignoreIds: ignoredTeams,
                      name: target.value
                    })
                  );
                }}
                onSearchReturnKey={() => {}}
                items={
                  has(teams, "data")
                    ? teams.data.map(t => ({
                        label: t.name,
                        tooltip: t.tooltip,
                        value: t.id
                      }))
                    : []
                }
                onSelect={item => {
                  if (teamWorkflowsLength === 0) {
                    onTeamSelect(item);
                    setShowTeamBox(false);
                    dispatch(
                      getAllTeams({
                        clientId,
                        ignoreIds: [item.value]
                      })
                    );
                  } else {
                    Notification.confirm(
                      Translate({
                        id: "selecting_this_item_will_remove_any_succeeding_workflows_proceed"
                      }),
                      {
                        labelOk: Translate({ id: "yes", uppercase: true }),
                        labelCancel: Translate({ id: "no", uppercase: true }),
                        onOk: () => {
                          onTeamSelect(item);
                          setShowTeamBox(false);
                          dispatch(
                            getAllTeams({
                              clientId,
                              ignoreIds: [item.value]
                            })
                          );
                          removeSucceedingWorkflows(item);
                        }
                      }
                    );
                  }
                }}
                selectedItems={
                  isEmpty(userWorkflow.destination_team)
                    ? []
                    : [userWorkflow.destination_team]
                }
                onRemoveItem={item => {
                  if (teamWorkflowsLength === 0) {
                    onTeamRemove(item);
                  } else {
                    Notification.confirm(
                      Translate({
                        id: "removing_this_item_will_remove_any_succeeding_workflows_proceed"
                      }),
                      {
                        labelOk: Translate({ id: "yes", uppercase: true }),
                        labelCancel: Translate({ id: "no", uppercase: true }),
                        onOk: () => {
                          onTeamRemove(item);
                          removeSucceedingWorkflows(item);
                        }
                      }
                    );
                  }
                }}
                errors={
                  isEmpty(userWorkflow.destination_team) ? errorWorkflow : {}
                }
                errorKey="workflows"
                allowMultiSelect={false}
              />
            ) : (
              <>
                <div className="team-source-destination">
                  {!isEmpty(userWorkflow.destination_team) ? (
                    <span className="assigned">
                      {userWorkflow.destination_team.label}
                      <div
                        className="change-team pointer mt-5"
                        onClick={() => {
                          if (teamWorkflowsLength === 0) {
                            setShowTeamBox(true);
                          } else {
                            Notification.confirm(
                              Translate({
                                id: "changing_this_item_will_remove_any_succeeding_workflows_proceed"
                              }),
                              {
                                labelOk: Translate({
                                  id: "yes",
                                  uppercase: true
                                }),
                                labelCancel: Translate({
                                  id: "no",
                                  uppercase: true
                                }),
                                onOk: () => {
                                  setShowTeamBox(true);
                                  removeSucceedingWorkflows(
                                    userWorkflow.destination_team
                                  );
                                }
                              }
                            );
                          }
                        }}
                      >
                        ({Translate({ id: "change" })} )
                      </div>
                    </span>
                  ) : (
                    <span
                      className="assign pointer"
                      onClick={() => setShowTeamBox(true)}
                    >
                      {Translate({ id: "click_to_assign_a_team" })}
                    </span>
                  )}
                </div>
                <ErrorLabel
                  errorKey={"workflows"}
                  errors={
                    isEmpty(userWorkflow.destination_team) ? errorWorkflow : {}
                  }
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

Desktop.propTypes = {
  clientId: PropTypes.string,
  userWorkflow: PropTypes.object,
  onUserSelect: PropTypes.func,
  onTeamSelect: PropTypes.func,
  onUserRemove: PropTypes.func,
  onTeamRemove: PropTypes.func,
  errorWorkflow: PropTypes.object
};

export default Desktop;
